import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Save, Check, Scale } from 'lucide-react';
import { FeaturedLayout } from '../layouts';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { getPlatformConfig, updatePlatformConfig, renderAvisoMyklubb, renderDpa, renderTosSaas } from '../../lib/supabase';
import { camposLegalesPlataformaFaltantes } from '../../legal/documents';
import LegalDocLink from '../legal/LegalDocLink';

const legalFromConfig = (data) => ({
  razon_social: data?.legal_entity?.razon_social || '',
  nombre_comercial: data?.legal_entity?.nombre_comercial || 'MYKLUBB',
  domicilio: data?.legal_entity?.domicilio || '',
  rfc: data?.legal_entity?.rfc || '',
  correo_privacidad: data?.legal_entity?.correo_privacidad || '',
  correo_contacto: data?.legal_entity?.correo_contacto || '',
  // Lo pide la cláusula de jurisdicción del Contrato de Servicio. Faltaba el
  // campo, así que el contrato que aceptan los clubes decía "[por definir]"
  // justo donde nombra los tribunales competentes.
  ciudad_jurisdiccion: data?.legal_entity?.ciudad_jurisdiccion || '',
});

const PlatformLegal = () => {
  const messageRef = useRef(null);
  const [form, setForm] = useState(legalFromConfig(null));
  const [savedForm, setSavedForm] = useState(legalFromConfig(null));
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const hasChanges = useMemo(
    () => Object.keys(form).some((key) => form[key] !== savedForm[key]),
    [form, savedForm]
  );

  // Lo que le falta a MYKLUBB para que SUS documentos esten completos. El alta de
  // organizaciones lee lo mismo y no deja crear clubes mientras falte algo.
  const faltanPlataforma = camposLegalesPlataformaFaltantes(form);

  useEffect(() => {
    const load = async () => {
      const { data } = await getPlatformConfig();
      if (data) {
        const initial = legalFromConfig(data);
        setForm(initial);
        setSavedForm(initial);
      }
      setLoading(false);
    };
    load();
  }, []);

  const handleChange = (field, value) => {
    setMessage(null);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const { data, error } = await updatePlatformConfig({ legal_entity: { ...form } });
      if (error) throw error;
      const updated = legalFromConfig(data);
      setForm(updated);
      setSavedForm(updated);
      setMessage({ type: 'success', text: 'Listo. Los datos legales se guardaron.', ts: Date.now() });
      setTimeout(() => messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
    } catch (err) {
      console.error('Error saving platform legal entity:', err);
      setMessage({ type: 'error', text: `Algo salio mal: ${err.message}`, ts: Date.now() });
      setTimeout(() => messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <FeaturedLayout title="Legal">
        <div className="p-6 text-center text-muted-foreground">Cargando...</div>
      </FeaturedLayout>
    );
  }

  return (
    <FeaturedLayout title="Legal">
      <div className="max-w-2xl mx-auto flex flex-col gap-6">
        {message && (
          <Alert
            ref={messageRef}
            key={message.ts}
            variant={message.type === 'success' ? 'success' : 'destructive'}
            className="animate-in fade-in duration-300"
          >
            <AlertDescription>{message.text}</AlertDescription>
          </Alert>
        )}

        {/* Entidad legal de MYKLUBB */}
        <div className="bg-background rounded-xl border p-6 flex flex-col gap-4">
          <div className="flex items-center gap-2 text-foreground font-semibold text-lg">
            <Scale />
            Entidad legal de MYKLUBB
          </div>
          <p className="text-xs text-muted-foreground -mt-2">
            Con estos datos se arman el Aviso de Privacidad de MYKLUBB, el Convenio de
            Tratamiento (DPA) y el Contrato de Servicio que acepta cada club. Son los tres
            documentos donde tú eres la contraparte.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="legal_razon_social">Razón social</Label>
              <Input
                id="legal_razon_social"
                value={form.razon_social}
                onChange={(e) => handleChange('razon_social', e.target.value)}
                placeholder="Razón social de MYKLUBB"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="legal_nombre_comercial">Nombre comercial</Label>
              <Input
                id="legal_nombre_comercial"
                value={form.nombre_comercial}
                onChange={(e) => handleChange('nombre_comercial', e.target.value)}
                placeholder="MYKLUBB"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="legal_rfc">RFC</Label>
              <Input
                id="legal_rfc"
                value={form.rfc}
                onChange={(e) => handleChange('rfc', e.target.value)}
                placeholder="RFC (opcional)"
              />
            </div>
            <div className="flex flex-col gap-1.5 sm:col-span-2">
              <Label htmlFor="legal_domicilio">Domicilio</Label>
              <Input
                id="legal_domicilio"
                value={form.domicilio}
                onChange={(e) => handleChange('domicilio', e.target.value)}
                placeholder="Domicilio de la entidad"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="legal_correo_privacidad">Correo ARCO / privacidad</Label>
              <Input
                id="legal_correo_privacidad"
                type="email"
                value={form.correo_privacidad}
                onChange={(e) => handleChange('correo_privacidad', e.target.value)}
                placeholder="privacidad@myklubb.com"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="legal_correo_contacto">Correo de contacto</Label>
              <Input
                id="legal_correo_contacto"
                type="email"
                value={form.correo_contacto}
                onChange={(e) => handleChange('correo_contacto', e.target.value)}
                placeholder="hola@myklubb.com"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="legal_ciudad_jurisdiccion">Ciudad para jurisdicción</Label>
              <Input
                id="legal_ciudad_jurisdiccion"
                value={form.ciudad_jurisdiccion}
                onChange={(e) => handleChange('ciudad_jurisdiccion', e.target.value)}
                placeholder="Torreón, Coahuila"
              />
              <p className="text-xs text-muted-foreground">
                Tribunales competentes en el Contrato de Servicio que aceptan los clubes.
              </p>
            </div>
          </div>

          {faltanPlataforma.length > 0 && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Falta {faltanPlataforma.join(', ')}. Mientras tanto, tus documentos salen con
              "[por definir]" y <strong>ningún club nuevo puede darse de alta</strong>: su
              administrador tendría que aceptar un contrato incompleto.
            </p>
          )}

          <div className="flex flex-wrap gap-4 pt-1">
            <LegalDocLink
              doc={renderAvisoMyklubb({
                razon_social: form.razon_social,
                domicilio: form.domicilio,
                correo_privacidad: form.correo_privacidad,
              })}
              label="Ver Aviso de Privacidad de MYKLUBB →"
            />
            <LegalDocLink
              doc={renderDpa({ razon_social: form.razon_social })}
              label="Ver Convenio de Tratamiento (DPA) →"
            />
            <LegalDocLink
              doc={renderTosSaas(form)}
              label="Ver Contrato de Servicio →"
            />
          </div>
        </div>

        {/* Guardar */}
        <div className="flex justify-end pb-6">
          <Button size="lg" onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? (
              <>
                <Save data-icon="inline-start" className="animate-spin" />
                Guardando...
              </>
            ) : !hasChanges ? (
              <>
                <Check data-icon="inline-start" />
                Sin cambios
              </>
            ) : (
              <>
                <Save data-icon="inline-start" />
                Guardar cambios
              </>
            )}
          </Button>
        </div>
      </div>
    </FeaturedLayout>
  );
};

export default PlatformLegal;
